import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { Car, ClipboardList, Download, Link2, Mail, Save, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/sonner";
import { trackEvent } from "@/lib/analytics";
import { SubpageShell } from "@/components/landing/SubpageShell";
import { CheckoutModal } from "@/components/landing/CheckoutModal";
import { Reveal } from "@/components/landing/Reveal";

const money = (n) => "$" + Math.round(n).toLocaleString();
const num = (n) =>
    n % 1 === 0 ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 1 });

const Field = ({ label, hint, value, onChange, min, max, step = 1, suffix, presets, testid }) => (
    <div>
        <div className="flex items-baseline justify-between gap-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                {label}
            </label>
            {presets && (
                <div className="flex gap-1">
                    {presets.map((p) => (
                        <button
                            key={p}
                            type="button"
                            data-testid={`${testid}-preset-${p}`}
                            onClick={() => onChange(p)}
                            className={`rounded-full px-2 py-0.5 text-[10px] font-bold transition-colors ${
                                value === p
                                    ? "bg-sky-400 text-[#0A0D14]"
                                    : "border border-white/15 text-slate-400 hover:border-sky-400/50"
                            }`}
                        >
                            {p}%
                        </button>
                    ))}
                </div>
            )}
        </div>
        <div className="mt-2 flex items-center gap-3">
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="h-1.5 flex-1 cursor-pointer accent-sky-400"
                data-testid={`${testid}-slider`}
            />
            <div className="relative w-24 shrink-0">
                <input
                    type="number"
                    min={min}
                    max={max}
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    data-testid={testid}
                    className="h-10 w-full rounded-lg border border-white/15 bg-[#0C1019] px-3 pr-7 text-sm font-semibold text-white focus:outline-none focus:ring-1 focus:ring-sky-400"
                />
                {suffix && (
                    <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                        {suffix}
                    </span>
                )}
            </div>
        </div>
        {hint && <p className="mt-1.5 text-[11px] leading-snug text-slate-600">{hint}</p>}
    </div>
);

const Stat = ({ label, value, sub, testid }) => (
    <div
        data-testid={testid}
        className="rounded-2xl border border-white/10 bg-[#121722] p-5"
    >
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            {label}
        </p>
        <p className="mt-2 font-display text-2xl font-bold text-white sm:text-3xl">
            {value}
        </p>
        {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
);

export default function Proforma() {
    const [params] = useSearchParams();
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [dealerName, setDealerName] = useState(params.get("name") || "");
    const [deals, setDeals] = useState(Number(params.get("d")) || 100);
    const [penetration, setPenetration] = useState(Number(params.get("pen")) || 60);
    const [salesRate, setSalesRate] = useState(Number(params.get("sr")) || 20);
    const [totalRos, setTotalRos] = useState(Number(params.get("ros")) || 1000);
    const [cpRos, setCpRos] = useState(Number(params.get("cp")) || 700);
    const [serviceRate, setServiceRate] = useState(Number(params.get("svr")) || 5);
    const [retention, setRetention] = useState(Number(params.get("ret")) || 24);
    const [earlyCancel, setEarlyCancel] = useState(
        params.get("ec") !== null && params.get("ec") !== "" ? Number(params.get("ec")) : 10
    );
    const [earlyWindow, setEarlyWindow] = useState(Number(params.get("ew")) || 3);

    useEffect(() => {
        trackEvent("page_view");
        let meta = document.head.querySelector('meta[name="robots"]');
        if (!meta) {
            meta = document.createElement("meta");
            meta.setAttribute("name", "robots");
            document.head.appendChild(meta);
        }
        meta.setAttribute("content", "noindex, nofollow");
    }, []);

    const calc = useMemo(() => {
        const ec = earlyCancel / 100;
        const leftover = deals * (1 - penetration / 100);
        const salesNew = (leftover * salesRate) / 100;
        const serviceNew = (cpRos * serviceRate) / 100;
        const monthly = salesNew + serviceNew;
        // Active policies in month m: recent cohorts (within the early-cancel
        // window) are fully active; older cohorts are reduced by earlyCancel %.
        const activeAt = (m) =>
            monthly * Math.min(m, earlyWindow) +
            monthly * (1 - ec) * Math.max(0, Math.min(m, retention) - earlyWindow);
        const months = Array.from({ length: 36 }, (_, i) => {
            const m = i + 1;
            const active = activeAt(m);
            const upfront = monthly * 50;
            const residual = active * 30;
            return { month: m, active, upfront, residual, revenue: upfront + residual };
        });
        let cum = 0;
        const withCum = months.map((r) => {
            cum += r.revenue;
            return { ...r, cumulative: cum };
        });
        const at = (m) => withCum[Math.min(m, 36) - 1];
        const ltv =
            50 + (ec * earlyWindow * 30 + (1 - ec) * retention * 30);
        return {
            leftover,
            salesNew,
            serviceNew,
            monthly,
            months: withCum,
            ltv,
            plateauMonth: retention,
            plateauMonthly: at(retention).revenue,
            plateauResidual: activeAt(retention) * 30,
            yearOne: at(12).cumulative,
            twoYear: at(24).cumulative,
            threeYear: at(36).cumulative,
        };
    }, [deals, penetration, salesRate, cpRos, serviceRate, retention, earlyCancel, earlyWindow]);

    const copyLink = async () => {
        const url = shareLink();
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Share link copied", {
                description: "Paste it into an email — the dealer sees these exact numbers.",
            });
        } catch {
            toast.error("Copy failed", { description: url });
        }
    };

    const buildPdf = async () => {
        const { jsPDF } = await import("jspdf");
        const doc = new jsPDF({ unit: "mm", format: "a4" });
        const date = new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });

        // Cover
        doc.setFillColor(10, 13, 20);
        doc.rect(0, 0, 210, 297, "F");
        try {
            const res = await fetch("/logo-stacked.png");
            const blob = await res.blob();
            const dataUrl = await new Promise((r) => {
                const fr = new FileReader();
                fr.onload = () => r(fr.result);
                fr.readAsDataURL(blob);
            });
            doc.addImage(dataUrl, "PNG", 60, 42, 90, 60);
        } catch {
            /* logo optional */
        }
        doc.setTextColor(248, 250, 252);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(30);
        doc.text("Profit Share Proforma", 105, 138, { align: "center" });
        doc.setFontSize(14);
        doc.setTextColor(56, 189, 248);
        doc.text("AdvantaShield Dealer Partnership Program", 105, 149, { align: "center" });
        doc.setDrawColor(56, 189, 248);
        doc.setLineWidth(0.8);
        doc.line(75, 156, 135, 156);
        doc.setFontSize(12);
        doc.setTextColor(226, 232, 240);
        doc.text(
            dealerName ? `Prepared for: ${dealerName}` : "Prepared for: Dealer Partner",
            105,
            172,
            { align: "center" }
        );
        doc.setFont("helvetica", "normal");
        doc.setTextColor(148, 163, 184);
        doc.setFontSize(11);
        doc.text(date, 105, 180, { align: "center" });
        doc.setFontSize(10);
        doc.text(
            "Subscription vehicle service contracts with residual income and zero chargebacks.",
            105,
            262,
            { align: "center" }
        );
        doc.text("Created by dealers, for dealers.", 105, 268, { align: "center" });

        // Page 2 — assumptions + results
        doc.addPage();
        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text("Assumptions", 20, 24);
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.4);

        const row = (label, value, y, bold = false) => {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10.5);
            doc.setTextColor(100, 116, 139);
            doc.text(label, 20, y);
            doc.setFont("helvetica", bold ? "bold" : "normal");
            doc.setTextColor(15, 23, 42);
            doc.text(String(value), 190, y, { align: "right" });
            doc.line(20, y + 2.5, 190, y + 2.5);
        };

        let y = 36;
        row("Retail car deals per month", num(deals), y); y += 9;
        row("Current VSC penetration", penetration + "%", y); y += 9;
        row("Deals without a VSC (the opportunity)", num(calc.leftover), y); y += 9;
        row("Opportunity conversion — retail", salesRate + "%", y); y += 9;
        row("Total service ROs per month", num(totalRos), y); y += 9;
        row("Customer-pay ROs per month", num(cpRos), y); y += 9;
        row("Opportunity conversion — service", serviceRate + "%", y); y += 9;
        row("Average policy retention", retention + " months (after early period)", y); y += 9;
        row("Early cancellations", earlyCancel + "% within first " + earlyWindow * 30 + " days", y); y += 9;
        row("Enrollment bonus (per policy)", "$50", y); y += 9;
        row("Monthly residual (per active policy)", "$30 — zero chargebacks", y); y += 14;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(15, 23, 42);
        doc.text("Projected Results", 20, y); y += 12;
        row("New AdvantaShield policies per month", num(calc.monthly), y, true); y += 9;
        row("   from retail sales", num(calc.salesNew) + " / mo", y); y += 8;
        row("   from the service drive", num(calc.serviceNew) + " / mo", y); y += 10;
        row("Upfront income per month", money(calc.monthly * 50), y); y += 9;
        row(`Residual income at plateau (month ${retention})`, money(calc.plateauResidual), y, true); y += 9;
        row("Total monthly income at plateau", money(calc.plateauMonthly), y, true); y += 9;
        row("Total income — first 12 months", money(calc.yearOne), y, true); y += 9;
        row("Total income — first 24 months", money(calc.twoYear), y, true); y += 9;
        row("Total income — first 36 months", money(calc.threeYear), y, true); y += 14;

        // Page 3 — month-by-month revenue projection
        doc.addPage();
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(15, 23, 42);
        doc.text("Month-by-Month Revenue Projection", 20, 24);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139);
        doc.text(
            `Residual income builds as policies accumulate, plateauing at month ${retention}. Zero chargebacks — every dollar shown is kept.`,
            20,
            31
        );

        let py = 42;
        doc.setFontSize(9.5);
        doc.text("Month", 20, py);
        doc.text("Active policies", 52, py);
        doc.text("Monthly income", 92, py);
        doc.text("Cumulative", 130, py);
        doc.text("Revenue bar", 162, py);
        py += 3;
        doc.setDrawColor(226, 232, 240);
        doc.line(20, py, 190, py);
        py += 6;

        const maxRev = calc.plateauMonthly || 1;
        calc.months.forEach((r) => {
            const plateau = r.month === retention;
            doc.setFont("helvetica", plateau ? "bold" : "normal");
            doc.setTextColor(15, 23, 42);
            doc.setFontSize(9.5);
            doc.text(plateau ? `M${r.month}  (plateau)` : `M${r.month}`, 20, py);
            doc.text(num(r.active), 52, py);
            doc.text(money(r.revenue), 92, py);
            doc.text(money(r.cumulative), 130, py);
            doc.setFillColor(56, 189, 248);
            const bw = Math.max(1.5, (r.revenue / maxRev) * 26);
            doc.rect(162, py - 2.6, bw, 3, "F");
            py += 6.6;
        });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(148, 163, 184);
        doc.text(
            "Projection only — assumes constant monthly enrollments and average retention. AdvantaShield | advantashield.com",
            20,
            290
        );

        return doc;
    };

    const exportPdf = async () => {
        const doc = await buildPdf();
        doc.save(
            `AdvantaShield-Proforma${dealerName ? "-" + dealerName.replace(/[^a-z0-9]+/gi, "-") : ""}.pdf`
        );
        toast.success("PDF downloaded", {
            description: "Cover sheet and full projection included.",
        });
    };

    const inputsPayload = () => ({
        dealerName,
        deals,
        penetration,
        salesRate,
        totalRos,
        cpRos,
        serviceRate,
        retention,
        earlyCancel,
        earlyWindow,
    });

    const shareLink = () => {
        const q = new URLSearchParams({
            name: dealerName,
            d: deals,
            pen: penetration,
            sr: salesRate,
            ros: totalRos,
            cp: cpRos,
            svr: serviceRate,
            ret: retention,
            ec: earlyCancel,
            ew: earlyWindow,
        });
        return `${window.location.origin}/proforma?${q.toString()}`;
    };

    const [saving, setSaving] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailTo, setEmailTo] = useState("");
    const [sendingEmail, setSendingEmail] = useState(false);

    const saveScenario = async () => {
        setSaving(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/proforma-scenarios`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name: dealerName, inputs: inputsPayload() }),
                }
            );
            if (!res.ok) throw new Error();
            toast.success("Scenario saved", {
                description: "Find it anytime in Admin → Saved Proformas.",
            });
        } catch {
            toast.error("Save failed", { description: "Please try again in a moment." });
        } finally {
            setSaving(false);
        }
    };

    const sendProformaEmail = async (e) => {
        e.preventDefault();
        setSendingEmail(true);
        try {
            const doc = await buildPdf();
            const pdfBase64 = doc.output("datauristring").split(",")[1];
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/proforma/email`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        to: emailTo,
                        link: shareLink(),
                        inputs: inputsPayload(),
                        pdf_base64: pdfBase64,
                    }),
                }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok)
                throw new Error(
                    typeof data.detail === "string" ? data.detail : "Send failed"
                );
            toast.success("Proforma emailed", {
                description: data.attached
                    ? "Sent with the PDF proforma attached."
                    : "Sent with the interactive proforma link.",
            });
            setEmailOpen(false);
            setEmailTo("");
        } catch (err) {
            toast.error("Could not send", { description: err.message });
        } finally {
            setSendingEmail(false);
        }
    };

    return (
        <SubpageShell onQuote={() => setCheckoutOpen(true)}>
            <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-sky-400">
                    Dealer Proforma — Private
                </p>
                <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
                    Profit share,{" "}
                    <span className="text-sky-400">projected to the plateau.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
                    Run the numbers for your store: retail deals, VSC penetration, and
                    service ROs in — residual income out. $50 per enrollment, $30 per
                    month per active policy, zero chargebacks.
                </p>
                <div className="mt-7 max-w-md">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        Prepared for (dealer name)
                    </label>
                    <input
                        data-testid="proforma-dealer-name"
                        type="text"
                        placeholder="e.g. Smith Auto Group"
                        value={dealerName}
                        onChange={(e) => setDealerName(e.target.value)}
                        className="mt-2 h-11 w-full rounded-lg border border-white/15 bg-[#0C1019] px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-400"
                    />
                </div>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Reveal>
                    <div className="rounded-3xl border border-white/10 bg-[#0C1019] p-7">
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-400/10 text-sky-400">
                                <Car className="h-5 w-5" />
                            </span>
                            <h2 className="font-display text-xl font-semibold text-white">
                                Retail Sales
                            </h2>
                        </div>
                        <div className="mt-7 flex flex-col gap-7">
                            <Field
                                label="Car deals per month"
                                value={deals}
                                onChange={setDeals}
                                min={10}
                                max={1000}
                                step={10}
                                testid="proforma-deals"
                            />
                            <Field
                                label="Current VSC penetration"
                                hint="Share of buyers who already leave with a contract."
                                value={penetration}
                                onChange={setPenetration}
                                min={0}
                                max={100}
                                suffix="%"
                                testid="proforma-penetration"
                            />
                            <Field
                                label="Opportunity conversion — retail"
                                hint="Share of the remaining non-buyers AdvantaShield converts. Example scenario uses 20% (a fifth)."
                                value={salesRate}
                                onChange={setSalesRate}
                                min={1}
                                max={25}
                                suffix="%"
                                presets={[3, 5, 20]}
                                testid="proforma-sales-rate"
                            />
                        </div>
                        <p className="mt-6 rounded-xl border border-sky-400/20 bg-sky-400/5 px-4 py-3 text-sm text-slate-300">
                            {num(calc.leftover)} deals leave without a VSC →{" "}
                            <strong className="text-sky-300">
                                {num(calc.salesNew)} new policies / month
                            </strong>
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.08}>
                    <div className="rounded-3xl border border-white/10 bg-[#0C1019] p-7">
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-400/10 text-sky-400">
                                <ClipboardList className="h-5 w-5" />
                            </span>
                            <h2 className="font-display text-xl font-semibold text-white">
                                Service Drive
                            </h2>
                        </div>
                        <div className="mt-7 flex flex-col gap-7">
                            <Field
                                label="Total ROs per month"
                                value={totalRos}
                                onChange={setTotalRos}
                                min={100}
                                max={5000}
                                step={50}
                                testid="proforma-ros"
                            />
                            <Field
                                label="Customer-pay ROs per month"
                                hint="ROs where the customer pays out of pocket."
                                value={cpRos}
                                onChange={setCpRos}
                                min={0}
                                max={5000}
                                step={50}
                                testid="proforma-cp-ros"
                            />
                            <Field
                                label="Opportunity conversion — service"
                                hint="Share of customer-pay ROs that attach a policy."
                                value={serviceRate}
                                onChange={setServiceRate}
                                min={1}
                                max={25}
                                suffix="%"
                                presets={[3, 5, 10]}
                                testid="proforma-service-rate"
                            />
                        </div>
                        <p className="mt-6 rounded-xl border border-sky-400/20 bg-sky-400/5 px-4 py-3 text-sm text-slate-300">
                            {num(cpRos)} customer-pay ROs →{" "}
                            <strong className="text-sky-300">
                                {num(calc.serviceNew)} new policies / month
                            </strong>
                        </p>
                    </div>
                </Reveal>
            </div>

            <Reveal>
                <div className="mt-6 rounded-3xl border border-white/10 bg-[#0C1019] p-7">
                    <h2 className="font-display text-xl font-semibold text-white">
                        Retention &amp; Cancellations
                    </h2>
                    <div className="mt-7 grid grid-cols-1 gap-7 md:grid-cols-3">
                        <Field
                            label="Average policy retention (months)"
                            hint="How long the average client keeps coverage after the early period — sets where residual income plateaus."
                            value={retention}
                            onChange={setRetention}
                            min={6}
                            max={36}
                            suffix="mo"
                            testid="proforma-retention"
                        />
                        <Field
                            label="Early cancellation rate"
                            hint="Share of new policies that cancel inside the early window (typically ~10% in the first 90 days)."
                            value={earlyCancel}
                            onChange={setEarlyCancel}
                            min={0}
                            max={40}
                            suffix="%"
                            presets={[0, 10, 20]}
                            testid="proforma-early-cancel"
                        />
                        <Field
                            label="Early-cancel window (months)"
                            hint="Cancellations above are assumed to happen by the end of this window (default 3 = 90 days)."
                            value={earlyWindow}
                            onChange={setEarlyWindow}
                            min={1}
                            max={12}
                            suffix="mo"
                            testid="proforma-early-window"
                        />
                    </div>
                </div>
            </Reveal>

            <Reveal>
                <h2 className="mt-14 flex items-center gap-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    <TrendingUp className="h-6 w-6 text-sky-400" />
                    The projection
                </h2>
                <div
                    data-testid="proforma-results"
                    className="mt-7 grid grid-cols-2 gap-4 lg:grid-cols-3"
                >
                    <Stat
                        label="New policies / month"
                        value={num(calc.monthly)}
                        sub={`${num(calc.salesNew)} retail + ${num(calc.serviceNew)} service`}
                        testid="proforma-stat-policies"
                    />
                    <Stat
                        label="Upfront income / month"
                        value={money(calc.monthly * 50)}
                        sub="$50 per enrollment"
                        testid="proforma-stat-upfront"
                    />
                    <Stat
                        label="Avg lifetime value / policy"
                        value={money(calc.ltv)}
                        sub={`after ${earlyCancel}% early cancellations`}
                        testid="proforma-stat-ltv"
                    />
                    <Stat
                        label={`Residual at plateau (mo ${retention})`}
                        value={money(calc.plateauResidual)}
                        sub={`${money(calc.plateauMonthly)}/mo total incl. upfront`}
                        testid="proforma-stat-plateau"
                    />
                    <Stat
                        label="First 12 months total"
                        value={money(calc.yearOne)}
                        testid="proforma-stat-year-one"
                    />
                    <Stat
                        label="First 24 months total"
                        value={money(calc.twoYear)}
                        sub={`36 months: ${money(calc.threeYear)}`}
                        testid="proforma-stat-total"
                    />
                </div>
                <p className="mt-5 inline-flex rounded-full border border-sky-400/30 bg-sky-400/10 px-4 py-2 text-sm font-semibold text-sky-300">
                    Every dollar above is kept — zero chargebacks, ever.
                </p>

                <div className="mt-8 h-72 rounded-3xl border border-white/10 bg-[#0C1019] p-5" data-testid="proforma-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={calc.months}>
                            <CartesianGrid stroke="#232b3a" strokeDasharray="3 3" />
                            <XAxis
                                dataKey="month"
                                tick={{ fill: "#64748b", fontSize: 11 }}
                                tickFormatter={(m) => `M${m}`}
                            />
                            <YAxis
                                tick={{ fill: "#64748b", fontSize: 11 }}
                                tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                                width={44}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: "#121722",
                                    border: "1px solid #232b3a",
                                    borderRadius: 12,
                                    fontSize: 12,
                                }}
                                labelFormatter={(m) => `Month ${m}`}
                                formatter={(v, name) => [
                                    money(v),
                                    name === "revenue" ? "Monthly income" : "Cumulative",
                                ]}
                            />
                            <ReferenceLine
                                x={retention}
                                stroke="#38bdf8"
                                strokeDasharray="4 4"
                                label={{
                                    value: "plateau",
                                    fill: "#38bdf8",
                                    fontSize: 11,
                                    position: "top",
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="revenue"
                                stroke="#38bdf8"
                                fill="#38bdf8"
                                fillOpacity={0.15}
                                strokeWidth={2}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </Reveal>

            <Reveal>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                    <button
                        data-testid="proforma-copy-link-btn"
                        onClick={copyLink}
                        className="inline-flex items-center gap-2.5 rounded-full border border-white/15 px-7 py-4 text-base font-semibold text-white transition-[border-color,background-color] duration-300 hover:border-sky-400/50 hover:bg-white/5"
                    >
                        <Link2 className="h-5 w-5" />
                        Copy share link
                    </button>
                    <button
                        data-testid="proforma-pdf-btn"
                        onClick={exportPdf}
                        className="inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-7 py-4 text-base font-bold text-[#0A0D14] transition-[background-color,box-shadow,transform] duration-300 hover:bg-sky-300 hover:shadow-[0_0_44px_rgba(56,189,248,0.5)] active:scale-95"
                    >
                        <Download className="h-5 w-5" />
                        Export PDF with cover sheet
                    </button>
                    <button
                        data-testid="proforma-email-btn"
                        onClick={() => setEmailOpen(true)}
                        className="inline-flex items-center gap-2.5 rounded-full border border-white/15 px-7 py-4 text-base font-semibold text-white transition-[border-color,background-color] duration-300 hover:border-sky-400/50 hover:bg-white/5"
                    >
                        <Mail className="h-5 w-5" />
                        Email to dealer
                    </button>
                    <button
                        data-testid="proforma-save-btn"
                        onClick={saveScenario}
                        disabled={saving}
                        className="inline-flex items-center gap-2.5 rounded-full border border-white/15 px-7 py-4 text-base font-semibold text-white transition-[border-color,background-color] duration-300 hover:border-sky-400/50 hover:bg-white/5 disabled:opacity-60"
                    >
                        <Save className="h-5 w-5" />
                        {saving ? "Saving…" : "Save scenario"}
                    </button>
                </div>
                <p className="mt-5 max-w-2xl text-xs leading-relaxed text-slate-600">
                    Projection for discussion purposes only — assumes constant monthly
                    enrollments and average retention of {retention} months. Actual
                    results vary by store.
                </p>
            </Reveal>

            <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                <DialogContent
                    data-testid="proforma-email-modal"
                    className="border-white/10 bg-[#121722] text-white sm:max-w-md"
                >
                    <DialogTitle className="font-display text-xl font-bold tracking-tight">
                        Email this proforma to a{" "}
                        <span className="text-sky-400">dealer</span>
                    </DialogTitle>
                    <p className="text-sm text-slate-400">
                        They'll receive a branded summary, the PDF proforma attached,
                        and a link to the live calculator with these numbers
                        pre-filled.
                    </p>
                    <form
                        onSubmit={sendProformaEmail}
                        className="mt-2 flex flex-col gap-4"
                    >
                        <input
                            data-testid="proforma-email-input"
                            type="email"
                            required
                            placeholder="dealer@theirstore.com"
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            className="h-11 w-full rounded-lg border border-white/15 bg-[#0C1019] px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-400"
                        />
                        <button
                            data-testid="proforma-email-send-btn"
                            type="submit"
                            disabled={sendingEmail}
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-sky-400 py-3.5 text-sm font-bold text-[#0A0D14] transition-[background-color,box-shadow] duration-300 hover:bg-sky-300 disabled:opacity-60"
                        >
                            <Mail className="h-4 w-4" />
                            {sendingEmail ? "Sending…" : "Send proforma email"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>

            <CheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} />
            <Toaster position="top-center" richColors />
        </SubpageShell>
    );
}
